/**
 * CityMapView.jsx — MapLibre GL JS · city/surface scope
 *
 * Renders passenger markers for driving, cycling, walking, rideshare, golf_cart,
 * transit, prm_vehicle, motorcycle sessions. OSRM route lines drawn per session.
 *
 * 3D buildings (OpenFreeMap liberty style → auto-detected OSM building extrusions),
 * satellite/street toggle (ESRI World Imagery), and pitch/2D toggle controls.
 *
 * Default center: JFK Terminal 5. Flies to Las Vegas when LV sessions are active
 * (detected from position lat/lng bounds).
 *
 * Uses JourneyQueueMapLibre class — same public API as Leaflet JourneyQueue.
 * queueRegistryRef Map() is shared across all three scope views in Index.jsx.
 *
 * Props:
 *   sessions          — array of serialized map session objects from controller
 *   queueRegistryRef  — useRef(new Map()) shared across all views
 *   isActive          — boolean; true when this tab is visible (triggers resize)
 *   destination       — { lat, lng } OSRM route destination override (optional)
 *
 * Dependencies (loaded by Index.jsx before this mounts):
 *   window.maplibregl  — MapLibre GL JS 4.7.1
 */

import React, { useEffect, useRef, useState, useCallback } from "react";
import { JourneyQueueMapLibre } from "../../../utils/JourneyQueueMapLibre.js";
import { PROPERTY_REGISTRY, propertiesForCity } from "../../../utils/PropertyRegistry.js";

// ── Constants ────────────────────────────────────────────────────────────────

const JFK_T5 = { lat: 40.6441, lng: -73.7840 };

// Las Vegas "downtown / Strip" bounding box — if any session is inside, fly there.
const LV_BOUNDS = { minLat: 35.95, maxLat: 36.30, minLng: -115.40, maxLng: -115.05 };

// Named city centers for the explicit city-selector.
// "auto" is the sentinel value meaning "derive from session positions".
const CITY_CENTERS = {
  JFK: { lat: 40.6413,  lng: -73.7781,   zoom: 13, label: "JFK · New York",         hasLv: false },
  BOS: { lat: 42.3636,  lng: -71.0096,   zoom: 13, label: "BOS · Boston Logan",      hasLv: false },
  LAS: { lat: 36.1070,  lng: -115.1730,  zoom: 14, label: "LAS · Las Vegas Strip",   hasLv: true  },
  ATL: { lat: 33.6407,  lng: -84.4277,   zoom: 13, label: "ATL · Atlanta",            hasLv: false },
  LAX: { lat: 33.9425,  lng: -118.4081,  zoom: 13, label: "LAX · Los Angeles",        hasLv: false },
};

const OPENFREEMAP_STYLE = "https://tiles.openfreemap.org/styles/liberty";

const ESRI_SATELLITE_SOURCE = {
  type:  "raster",
  tiles: ["https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"],
  tileSize: 256,
  attribution: "© Esri, Maxar, Earthstar Geographics",
};
const ESRI_SATELLITE_LAYER = {
  id:     "esri-satellite",
  type:   "raster",
  source: "esri-satellite",
  layout: { visibility: "none" },
};

// ── LV venue markers ─────────────────────────────────────────────────────────

const LV_VENUES = [
  { id: "mgm",        label: "MGM Grand",           lng: -115.1672, lat: 36.1026 },
  { id: "allegiant",  label: "Allegiant Stadium",    lng: -115.1833, lat: 36.0905 },
  { id: "tmobile",    label: "T-Mobile Arena",       lng: -115.1776, lat: 36.1031 },
  { id: "sphere",     label: "The Sphere",           lng: -115.1641, lat: 36.1205 },
  { id: "venetian",   label: "Venetian Hotel",       lng: -115.1690, lat: 36.1215 },
  { id: "venetian_expo","label":"Venetian Expo",     lng: -115.1702, lat: 36.1252 },
  { id: "lvcc",       label: "LVCC",                 lng: -115.1523, lat: 36.1357 },
  { id: "topgolf_mgm","label":"Top Golf MGM",        lng: -115.1720, lat: 36.0989 },
];

// City center for LV — Strip center near T-Mobile Arena
const LV_CENTER = { lat: 36.1070, lng: -115.1730 };

// ── Small helper ─────────────────────────────────────────────────────────────

function inLvBounds(pos) {
  if (!pos) return false;
  return (
    pos.lat >= LV_BOUNDS.minLat && pos.lat <= LV_BOUNDS.maxLat &&
    pos.lng >= LV_BOUNDS.minLng && pos.lng <= LV_BOUNDS.maxLng
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function CityMapView({ sessions = [], queueRegistryRef, isActive, destination, selectedCity, onCitySelect, onPropertySelect }) {
  const containerRef  = useRef(null);
  const mapRef        = useRef(null);
  const markersAddedRef = useRef(new Set());
  const venueMarkersRef = useRef([]);

  // Keep a stable ref so property-marker click handlers always get the latest callback.
  const onPropertySelectRef = useRef(onPropertySelect);
  useEffect(() => { onPropertySelectRef.current = onPropertySelect; }, [onPropertySelect]);

  const [satellite, setSatellite] = useState(false);
  const [pitch3d,   setPitch3d]   = useState(false);
  const [buildings, setBuildings] = useState(true);

  // ── Map init ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!window.maplibregl || !containerRef.current) return;

    const maplibregl = window.maplibregl;

    const map = new maplibregl.Map({
      container:   containerRef.current,
      style:       OPENFREEMAP_STYLE,
      center:      [JFK_T5.lng, JFK_T5.lat],
      zoom:         13,
      pitch:        0,
      bearing:      0,
      attributionControl: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: true }), "top-right");
    map.addControl(new maplibregl.ScaleControl({ unit: "imperial" }),       "bottom-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }),    "bottom-left");

    // Add satellite source + layer after style loads
    map.on("style.load", () => {
      // Satellite raster source
      if (!map.getSource("esri-satellite")) {
        map.addSource("esri-satellite", ESRI_SATELLITE_SOURCE);
        map.addLayer(ESRI_SATELLITE_LAYER);
      }

      // 3D building extrusion — detect source from the style automatically
      _add3dBuildings(map);
    });

    mapRef.current = map;

    return () => {
      // Remove all passenger markers before destroying map
      queueRegistryRef.current.forEach((entry) => {
        if (entry?.cityJq) {
          entry.cityJq.removeFromMap();
          entry.cityJq = null;
        }
      });
      markersAddedRef.current.clear();

      // Remove LV venue markers
      venueMarkersRef.current.forEach((m) => m.remove());
      venueMarkersRef.current = [];

      map.remove();
      mapRef.current = null;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Resize when tab becomes active ───────────────────────────────────────

  useEffect(() => {
    if (!isActive || !mapRef.current) return;
    const t = setTimeout(() => mapRef.current?.resize(), 50);
    return () => clearTimeout(t);
  }, [isActive]);

  // ── Explicit city select — fires when user clicks an airport label on World map
  //    or uses the city pill-bar buttons in this view's toolbar ──────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !selectedCity) return;

    const city = CITY_CENTERS[selectedCity];
    if (!city) return;

    map.flyTo({ center: [city.lng, city.lat], zoom: city.zoom, speed: 0.8, essential: true });

    // Add clickable property/venue label chips for the selected city.
    // Replaces the old _addLvVenueMarkers (LAS-only, non-interactive).
    // Now covers JFK, BOS, and LAS — any city with PROPERTY_REGISTRY entries.
    _addPropertyMarkers(map, venueMarkersRef, selectedCity, onPropertySelectRef);
  }, [selectedCity]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-fly based on active sessions (only when no explicit city is pinned) ──

  useEffect(() => {
    const map = mapRef.current;
    if (!map || selectedCity) return;   // manual pin takes precedence

    const citySessions = sessions.filter((s) => s.scope === "city");
    if (!citySessions.length) return;

    const hasLv = citySessions.some((s) => inLvBounds(s.position));
    const target = hasLv ? LV_CENTER : JFK_T5;
    const zoom   = hasLv ? 14 : 13;

    map.flyTo({ center: [target.lng, target.lat], zoom, speed: 0.8, essential: true });

    // Add property markers for the auto-detected city (LAS or JFK).
    _addPropertyMarkers(map, venueMarkersRef, hasLv ? "LAS" : "JFK", onPropertySelectRef);
  }, [sessions, selectedCity]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Sync passenger markers ────────────────────────────────────────────────

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const citySessions = sessions.filter((s) => s.scope === "city");
    const activeIds    = new Set(citySessions.map((s) => String(s.id)));

    // Remove stale markers
    markersAddedRef.current.forEach((id) => {
      if (!activeIds.has(id)) {
        const entry = queueRegistryRef.current.get(id);
        if (entry?.cityJq) {
          entry.cityJq.removeFromMap();
          entry.cityJq = null;
        }
        markersAddedRef.current.delete(id);
      }
    });

    // Add or update
    citySessions.forEach((sessionData) => {
      const id    = String(sessionData.id);
      const entry = queueRegistryRef.current.get(id) || {};
      const dest  = destination || JFK_T5;

      if (!markersAddedRef.current.has(id)) {
        // New session — create JQ instance and add to map
        const jq = new JourneyQueueMapLibre(sessionData);
        entry.cityJq = jq;
        queueRegistryRef.current.set(id, entry);
        markersAddedRef.current.add(id);

        const doAdd = () => jq.addToMap(map, dest);
        if (map.isStyleLoaded()) {
          doAdd();
        } else {
          map.once("style.load", doAdd);
        }
      } else if (entry?.cityJq) {
        // Existing session — position may have updated
        entry.cityJq.updatePosition(sessionData.position, sessionData.alert_color);
      }
    });
  }, [sessions, destination]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Satellite toggle ──────────────────────────────────────────────────────

  const toggleSatellite = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const next = !satellite;
    setSatellite(next);

    if (map.getLayer("esri-satellite")) {
      map.setLayoutProperty("esri-satellite", "visibility", next ? "visible" : "none");
    }

    // Hide/show vector building fill while satellite is active
    ["building", "building-top"].forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", next ? "none" : "visible");
      }
    });
  }, [satellite]);

  // ── 3D pitch toggle ───────────────────────────────────────────────────────

  const toggle3d = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const next = !pitch3d;
    setPitch3d(next);
    map.easeTo({ pitch: next ? 50 : 0, duration: 500 });
  }, [pitch3d]);

  // ── 3D buildings visibility ───────────────────────────────────────────────

  const toggleBuildings = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const next = !buildings;
    setBuildings(next);

    // Toggle both the flat footprint fill and the 3D extrusion
    ["3d-buildings-extrusion", "building-footprint-fill"].forEach((id) => {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, "visibility", next ? "visible" : "none");
      }
    });

    // Auto-tilt to 45° when buildings are enabled from a flat view
    // so the 3D depth is immediately apparent
    if (next && map.getPitch() < 5) {
      map.easeTo({ pitch: 45, duration: 600 });
      setPitch3d(true);
    }
  }, [buildings]);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div style={styles.root}>
      {/* Map canvas */}
      <div ref={containerRef} style={styles.map} />

      {/* Tool bar */}
      <div style={styles.toolbar}>
        <ToolButton active={satellite} onClick={toggleSatellite} title="Satellite" emoji="🛰" />
        <ToolButton active={pitch3d}   onClick={toggle3d}        title="3D"        emoji="🏙" />
        <ToolButton active={buildings} onClick={toggleBuildings}  title="Buildings" emoji="🏢" />

        {/* City selector — one pill per city; fires onCitySelect to sync Index state */}
        {onCitySelect && (
          <div style={styles.cityPills}>
            {Object.entries(CITY_CENTERS).map(([code, city]) => {
              const active = selectedCity === code ||
                             (!selectedCity && code === "LAS" && sessions.some((s) => s.scope === "city" && inLvBounds(s.position))) ||
                             (!selectedCity && code === "JFK" && !sessions.some((s) => s.scope === "city" && inLvBounds(s.position)));
              return (
                <button
                  key={code}
                  onClick={() => onCitySelect(code)}
                  title={city.label}
                  style={{
                    ...styles.cityPill,
                    background:  active ? "rgba(99,102,241,0.9)"  : "rgba(7,9,15,0.82)",
                    borderColor: active ? "#818CF8"                : "#374151",
                    color:       active ? "#E0E0F0"                : "#9CA3AF",
                  }}
                >
                  {code}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Pulse ring keyframes (injected once) */}
      <PulseStyle />
    </div>
  );
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ToolButton({ active, onClick, title, emoji }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        ...styles.toolBtn,
        background: active ? "rgba(99,102,241,0.9)" : "rgba(7,9,15,0.82)",
        borderColor: active ? "#818CF8" : "#374151",
      }}
    >
      {emoji}
    </button>
  );
}

let pulseInjected = false;
function PulseStyle() {
  if (typeof document === "undefined" || pulseInjected) return null;
  pulseInjected = true;
  const style = document.createElement("style");
  style.textContent = `
    .jq-pulse-ring {
      position: absolute;
      inset: -4px;
      border-radius: 50%;
      border: 2px solid currentColor;
      opacity: 0;
      animation: jqPulse 2.2s ease-out infinite;
    }
    @keyframes jqPulse {
      0%   { transform: scale(0.8); opacity: 0.7; }
      70%  { transform: scale(1.6); opacity: 0; }
      100% { transform: scale(1.6); opacity: 0; }
    }
    .maplibregl-popup-content {
      background: #111827 !important;
      color: #E0E0F0 !important;
      border: 1px solid #374151 !important;
      border-radius: 8px !important;
      padding: 10px 12px !important;
      box-shadow: 0 4px 16px rgba(0,0,0,0.7) !important;
    }
    .maplibregl-popup-tip {
      border-top-color: #111827 !important;
    }
    .maplibregl-popup-close-button {
      color: #6B7280 !important;
      font-size: 16px !important;
      right: 6px !important;
      top: 4px !important;
    }
  `;
  document.head.appendChild(style);
  return null;
}

// ── Private helpers ───────────────────────────────────────────────────────────

function _add3dBuildings(map) {
  // OpenFreeMap / OpenMapTiles buildings use render_height / render_min_height
  // with NO "extrude" property — filtering on extrude matches nothing.
  // We add two layers:
  //   1. building-footprint-fill — flat polygon always visible (pitch 0 or 3D)
  //   2. 3d-buildings-extrusion  — fill-extrusion, visible when pitch > 0
  try {
    const layers    = map.getStyle().layers || [];
    const baseLayer = layers.find((l) => l["source-layer"] === "building");
    if (!baseLayer) return;

    const source = baseLayer.source;

    // ── 1. Flat footprint fill ──
    if (!map.getLayer("building-footprint-fill")) {
      map.addLayer({
        id:             "building-footprint-fill",
        type:           "fill",
        source:         source,
        "source-layer": "building",
        paint: {
          "fill-color":         "#1E293B",
          "fill-opacity":       0.6,
          "fill-outline-color": "#334155",
        },
      });
    }

    // ── 2. 3D extrusion ──
    if (!map.getLayer("3d-buildings-extrusion")) {
      map.addLayer({
        id:             "3d-buildings-extrusion",
        type:           "fill-extrusion",
        source:         source,
        "source-layer": "building",
        // No extrude filter — show all buildings; use coalesce for height fallback
        paint: {
          "fill-extrusion-color": [
            "interpolate", ["linear"],
            ["coalesce", ["get", "render_height"], ["get", "height"], 0],
            0,   "#1E293B",
            50,  "#1E3A5F",
            100, "#1E4060",
            200, "#1B4F72",
          ],
          "fill-extrusion-height": ["coalesce", ["get", "render_height"], ["get", "height"], 8],
          "fill-extrusion-base":   ["coalesce", ["get", "render_min_height"], 0],
          "fill-extrusion-opacity": 0.72,
          "fill-extrusion-vertical-gradient": true,
        },
      });
    }
  } catch { /* style may not have building source */ }
}

/**
 * _addPropertyMarkers — add clickable venue / property label chips to the city map.
 *
 * Replaces the old _addLvVenueMarkers (LAS-only, non-interactive).
 * Now covers all cities with PROPERTY_REGISTRY entries (JFK, BOS, LAS).
 * When onPropertySelectRef.current is set, each chip shows "Label ↗" and fires
 * onPropertySelect(code) on click — switching to the Property tab focused on that venue.
 *
 * @param {maplibregl.Map} map
 * @param {React.MutableRefObject<maplibregl.Marker[]>} venueMarkersRef
 * @param {string} cityCode — "JFK" | "BOS" | "LAS"
 * @param {React.MutableRefObject<Function|undefined>} onPropertySelectRef
 */
function _addPropertyMarkers(map, venueMarkersRef, cityCode, onPropertySelectRef) {
  // Remove previous city's markers
  venueMarkersRef.current.forEach((m) => m.remove());
  venueMarkersRef.current = [];

  if (!cityCode) return;

  const maplibregl = window.maplibregl;
  const clickable  = !!onPropertySelectRef?.current;
  const codes      = propertiesForCity(cityCode);

  codes.forEach((code) => {
    const prop = PROPERTY_REGISTRY[code];
    if (!prop) return;

    const el = document.createElement("div");
    el.style.cssText = `
      background: rgba(7,9,15,0.88);
      border: 1.5px solid ${clickable ? "#818CF8" : "#6366F1"};
      border-radius: 6px;
      padding: 3px 8px;
      font: 700 10px/1.5 system-ui,sans-serif;
      color: #A5B4FC;
      white-space: nowrap;
      cursor: ${clickable ? "pointer" : "default"};
      box-shadow: 0 1px 6px rgba(0,0,0,0.6);
      pointer-events: ${clickable ? "auto" : "none"};
      transition: border-color 0.15s, background 0.15s, color 0.15s;
      user-select: none;
    `;

    const venueEmoji = prop.emoji ? `${prop.emoji} ` : "";
    el.textContent = clickable ? `${venueEmoji}${prop.label} ↗` : `${venueEmoji}${prop.label}`;
    el.title       = clickable ? `Focus Property: ${prop.label}` : prop.label;

    if (clickable) {
      el.addEventListener("mouseenter", () => {
        el.style.borderColor = "#A5B4FC";
        el.style.background  = "rgba(99,102,241,0.28)";
        el.style.color       = "#C7D2FE";
      });
      el.addEventListener("mouseleave", () => {
        el.style.borderColor = "#818CF8";
        el.style.background  = "rgba(7,9,15,0.88)";
        el.style.color       = "#A5B4FC";
      });
      el.addEventListener("click", () => {
        onPropertySelectRef.current?.(code);
      });
    }

    const marker = new maplibregl.Marker({ element: el, anchor: "bottom" })
      .setLngLat([prop.lng, prop.lat])
      .addTo(map);

    venueMarkersRef.current.push(marker);
  });
}

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  root: {
    position: "relative",
    flex:     1,
    display:  "flex",
    flexDirection: "column",
    minHeight: 0,
    background: "#07090F",
  },
  map: {
    flex: 1,
    minHeight: 0,
  },
  toolbar: {
    position:   "absolute",
    top:        12,
    left:       12,
    display:    "flex",
    flexDirection: "column",
    gap:        6,
    zIndex:     10,
  },
  toolBtn: {
    width:        34,
    height:       34,
    borderRadius: 8,
    border:       "1px solid #374151",
    color:        "#E0E0F0",
    fontSize:     16,
    cursor:       "pointer",
    display:      "flex",
    alignItems:   "center",
    justifyContent: "center",
    transition:   "border-color 0.15s, background 0.15s",
  },
  cityPills: {
    display:       "flex",
    flexDirection: "column",
    gap:           4,
    marginTop:     6,
    borderTop:     "1px solid #1F2937",
    paddingTop:    6,
  },
  cityPill: {
    width:        34,
    height:       22,
    borderRadius: 5,
    border:       "1px solid #374151",
    fontSize:     9,
    fontWeight:   700,
    fontFamily:   "monospace",
    cursor:       "pointer",
    display:      "flex",
    alignItems:   "center",
    justifyContent: "center",
    transition:   "all 0.15s",
    letterSpacing: "0.04em",
  },
};
